import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import React, { useState, useEffect, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { OutlierCardsColumn } from './components/OutlierCards';
import OutlierTableExpander from './components/OutlierTable/OutlierTableExpander';
import {
  FilterValues,
  FilterStatusType,
  UniversalFilterValue,
  FilterKey,
} from './types/types';
import { ContactStatusOptions } from '@amzn/aet-contactrecommendation-kit';
import { Profile } from '../../Types';
import {
  getTotalOutliers,
  getStatusText,
  transformOutliersToCategories,
  sortCategories,
  isZeroToleranceOutlier,
  isPendingContact,
  getPastWeek,
  calculateRequiredReviewsData,
} from './helpers';
import Text from '@amzn/meridian/text';
import Alert from '@amzn/meridian/alert';
import Loader from '@amzn/meridian/loader';
import { FilterPanel } from './components/OutlierFilter';
import { ActiveFilterTags } from './components/ActiveFilterTags';
import { useOutlierFilters } from './hooks/useOutlierFilters';
import { OutlierTimeOption } from '@amzn/aet-dynamo-kit';
import { MAX_THUMBNAIL_DISPLAY } from './types/constants';
import moment from 'moment';
import UserInfoSelect from '../Dashboard/DashboardComponents/UserInfoSelect';
import { weeksList } from '../../Util';
import useProxy from '../../hooks/useProxy';
import useInspireOptions from '../../hooks/useInspireOptions';
import useOutliersForManager from '../../api/Outlier/useOutliersForManager';

interface OutlierManagementTMHomeProps {
  profile: Profile;
  profileState: string;
}

const OutlierManagementTMHome: React.FC<OutlierManagementTMHomeProps> = ({
  profile,
  profileState,
}) => {
  const intl = useIntl();
  const pastWeek = getPastWeek(); // The latest data belong to last week
  const [weekValue, setWeekValue] = useState(pastWeek);
  const [weekChanged, setWeekChanged] = useState(false);
  const { isTm, isOm, requesterUserID, requesterUsername, isExclusivelyOm } =
    useInspireOptions();
  const { isProxying } = useProxy();

  const {
    loading: outliersLoading,
    error: outliersError,
    data: outliers,
  } = useOutliersForManager(
    profile.user.userName,
    false,
    OutlierTimeOption.WEEKLY,
    [weekValue]
  );

  const outlierCategories = useMemo(() => {
    const transformed = transformOutliersToCategories(outliers);
    return transformed;
  }, [outliers]);

  const associatesWithOutliersSample = useMemo(() => {
    const uniqueAssociates = new Map<string, { login: string; id: string }>();

    outlierCategories.forEach(category => {
      category.contacts
        .filter(contact => isPendingContact(contact.status))
        .forEach(contact => {
          if (!uniqueAssociates.has(contact.user.login)) {
            uniqueAssociates.set(contact.user.login, {
              login: contact.user.login,
              id: contact.user.id,
            });
          }
        });
    });

    return Array.from(uniqueAssociates.values()).slice(
      0,
      MAX_THUMBNAIL_DISPLAY
    );
  }, [outlierCategories]);

  const categories = useMemo(
    () => [...new Set(outlierCategories.map(cat => cat.title))],
    [outlierCategories]
  );

  const associates = useMemo(
    () => [
      ...new Set(
        outlierCategories.flatMap(cat =>
          cat.contacts.map(contact => contact.user.login)
        )
      ),
    ],
    [outlierCategories]
  );

  // Create a set of valid CSA logins from the profile's direct reports
  // This ensures we only count required reviews for CSAs who currently report to this TM
  const validCSALogins = useMemo(() => {
    return new Set(profile.directReports.map(dr => dr.userName));
  }, [profile.directReports]);

  const requiredReviewsData = useMemo(
    () => calculateRequiredReviewsData(outlierCategories, validCSALogins),
    [outlierCategories, validCSALogins]
  );

  const {
    filters,
    updateFilters,
    clearFilters,
    removeFilter,
    filterCategories: filterCategoriesByFilters,
  } = useOutlierFilters({
    initiaViewDataBy: OutlierTimeOption.WEEKLY,
    initialTimeOption: pastWeek,
    initialTimeOptions: [pastWeek],
    categories,
    associates,
    tmList: [],
    initialStatus: ContactStatusOptions.UNDEFINED,
  });

  // TODO: const weekValue = filters.week;
  // Potentially just reference filters.week instead of the useeffect flow

  useEffect(() => {
    if (filters.timeOption && filters.timeOption !== weekValue) {
      setWeekValue(filters.timeOption);
      setWeekChanged(filters.timeOption !== pastWeek);
    }
  }, [filters.timeOption, pastWeek, weekValue]);

  const filteredCategories = filterCategoriesByFilters(outlierCategories);
  const sortedFilteredCategories = useMemo(
    () => sortCategories(filteredCategories, filters.status),
    [filteredCategories, filters.status]
  );

  const handleFilterChange = (newFilters: Partial<FilterValues>) => {
    updateFilters(newFilters);
  };

  const getOutlierCountText = (
    count: number,
    status: FilterStatusType
  ): React.ReactNode => {
    const statusMessage = getStatusText(status);
    const statusText = intl.formatMessage(statusMessage);
    const [year, week] = weekValue.split('-');

    const weekStart = moment()
      .year(parseInt(year))
      .week(parseInt(week))
      .startOf('week');

    return (
      <FormattedMessage
        id='cs_perfportal_required_outlier_count'
        defaultMessage='Results for week of {month} {day} (Week {year}-{week})'
        values={{
          count,
          status: statusText,
          month: weekStart.format('MMMM'),
          day: weekStart.format('D'),
          year,
          week,
        }}
      />
    );
  };

  const displayFilters = {
    ...filters,
    [FilterKey.TIME_OPTION]: weekChanged ? filters.timeOption : undefined,
  };

  const renderMainContent = () => {
    if (outliersLoading) {
      return (
        <Row alignmentHorizontal='center'>
          <Loader type='circular' size='large' />
        </Row>
      );
    }

    if (outliersError) {
      return (
        <Alert type='error'>
          <FormattedMessage
            id='cs_perfportal_error_reason_1'
            defaultMessage='Your credentials may be incorrect or inadequate.'
          />
        </Alert>
      );
    }

    return (
      <>
        {isProxying && (
          <Box spacingInset='400 500 200 500'>
            <UserInfoSelect
              givenName={profile.user.displayName}
              username={profile.user.userName}
              weeks={weeksList()}
              selectedWeek={weekValue}
            />
          </Box>
        )}
        {isExclusivelyOm && (
          <Box spacingInset='400 500 500 500'>
            <Text type='h400'>
              <FormattedMessage
                id='cs_perfportal_om_outlier_metrics'
                defaultMessage='Outlier Metrics'
              />
            </Text>
          </Box>
        )}

        <Box
          spacingInset={
            isExclusivelyOm ? `none 500 500 none` : `none 500 500 500`
          }
        >
          <Row
            spacing='large'
            alignmentVertical='top'
            widths={['grid-4', 'grid-8']}
          >
            <Column spacingInset='none 500 none none'>
              <Box>
                <OutlierCardsColumn
                  onFilterChange={handleFilterChange}
                  outlierCategories={outlierCategories}
                  associatesWithOutliers={associatesWithOutliersSample}
                  validCSALogins={validCSALogins}
                />
              </Box>
            </Column>

            <Column>
              <Box width='100%'>
                <Row widths='fill' alignmentVertical='center'>
                  <Column>
                    <Row widths='fill' alignmentVertical='center'>
                      <Box type='fill' spacingInset='100 100 100 100'>
                        <Row
                          alignmentHorizontal='justify'
                          alignmentVertical='center'
                        >
                          <Column spacingInset='none none none 400'>
                            <Text type='h50'>
                              {getOutlierCountText(
                                getTotalOutliers(
                                  sortedFilteredCategories,
                                  filters.status
                                ),
                                filters.status
                              )}
                            </Text>
                          </Column>
                          <FilterPanel
                            categories={categories}
                            associates={associates}
                            pastWeek={pastWeek}
                            filters={filters}
                            onFilterChange={handleFilterChange}
                            onClearFilters={() => {
                              clearFilters();
                              setWeekChanged(false);
                            }}
                          />
                        </Row>
                      </Box>
                    </Row>

                    <Row spacingInset='200 none 300 none'>
                      <ActiveFilterTags
                        activeFilters={displayFilters}
                        onRemoveFilter={key => {
                          if (key === FilterKey.TIME_OPTION) {
                            setWeekChanged(false);
                          }
                          removeFilter(key);
                        }}
                      />
                    </Row>
                  </Column>
                </Row>
              </Box>

              <Column>
                <Box width='100%'>
                  <Text type='b200'>
                    <FormattedMessage
                      id='cs_perfportal_outlier_business_rule'
                      defaultMessage='Review minimum of 1 contact per associate per zero tolerance category. For non-zero tolerance categories, review at least 2 contacts per associate.'
                    />
                  </Text>
                  <Box height='100%'>
                    <Column spacing='medium'>
                      {sortedFilteredCategories.length === 0 ? (
                        <Text type='b200'>
                          <FormattedMessage
                            id='cs_perfportal_no_results'
                            defaultMessage='No results found'
                          />
                        </Text>
                      ) : (
                        sortedFilteredCategories.map(category => {
                          const contactCount = category.contacts.filter(
                            contact => {
                              if (filters.status === UniversalFilterValue.ALL)
                                return true;
                              return contact.status === filters.status;
                            }
                          ).length;

                          // Get the required reviews remaining for this category
                          const requiredReviewsRemaining =
                            requiredReviewsData.byCategory[category.title]
                              ?.remainingRequired;

                          // Create a map of associate login to required reviews remaining for this category
                          const associateRequiredReviews: Record<
                            string,
                            number
                          > = {};

                          // Find associates with this category in their required reviews
                          requiredReviewsData.byAssociate.forEach(associate => {
                            if (associate.categories[category.title]) {
                              associateRequiredReviews[associate.login] =
                                associate.categories[
                                  category.title
                                ].remainingRequired;
                            }
                          });

                          return (
                            <OutlierTableExpander
                              key={category.title}
                              category={category}
                              pendingCount={contactCount}
                              isZeroTolerance={isZeroToleranceOutlier(
                                category.title
                              )}
                              selectedStatus={filters.status}
                              requiredReviewsRemaining={
                                requiredReviewsRemaining ?? 0
                              }
                              associateRequiredReviews={
                                associateRequiredReviews
                              }
                              profile={profile}
                              clickstreamTags={{
                                'data-csa-c-profile-id': requesterUserID,
                                'data-csa-c-username': requesterUsername,
                                'data-csa-c-is-TM': isTm,
                                'data-csa-c-is-OM': isOm,
                              }}
                            />
                          );
                        })
                      )}
                    </Column>
                  </Box>
                </Box>
              </Column>
            </Column>
          </Row>
        </Box>
      </>
    );
  };

  return (
    <Box maxWidth='100%' overflowX='hidden'>
      {renderMainContent()}
    </Box>
  );
};

export default OutlierManagementTMHome;
